import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Dimensions, FlatList, ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { BaseSkeleton } from '../../atoms/baseSkeleton/BaseSkeleton'
import { ArrowIcon, ArrowIconDirection } from '../../atoms/icons/ArrowIcon'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { SecondaryTertiaryIconButton } from '../../atoms/secondaryTertiaryIconButton/SecondaryTertiaryIconButton'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CarouselSliderProps<T> {
  cards: T[]
  maxCards: number
  renderCardItems: ({ item }: { item: T }) => React.JSX.Element
  onIndexChange: (index: number) => void
  showButtons: boolean
  showChevronIcons?: boolean
  containerStyle?: ViewStyle
  flatListContainerStyle?: ViewStyle
  buttonStyle?: ViewStyle
  shouldShowButtonsInTopRight?: boolean
  showCarouselButtonSkeleton?: boolean
}

const BodyContainer = styled.View(({}) => ({
  flexDirection: 'column',
}))

const FlatListContainer = styled.View<{ theme: ThemeType; showChevronIcons: boolean }>(
  ({ theme, showChevronIcons }) => ({
    width: '100%',
    marginBottom: showChevronIcons ? theme.spacing['12px'] : theme.breakpoints.isMinWidthLaptop ? '55px' : 0,
  }),
)

const CarouselButtonContainer = styled.View<{
  theme: ThemeType
  showChevronIcons: boolean
  shouldShowButtonsInTopRight: boolean
}>(({ theme, showChevronIcons, shouldShowButtonsInTopRight }) => ({
  flexDirection: 'row',
  margin: 'auto',
  ...(showChevronIcons && { margin: 0, alignSelf: 'flex-end', gap: theme.spacing['8px'] }),
  ...(shouldShowButtonsInTopRight && { position: 'absolute', right: theme.spacing['16px'], top: '-56px' }),
}))

const CarouselLeftButtonContainer = styled.View<{ theme: ThemeType; showChevronIcons: boolean }>(
  ({ theme, showChevronIcons }) => ({
    marginRight: showChevronIcons ? '6px' : theme.spacing['24px'],
  }),
)

export const CarouselSlider = <T,>({
  cards,
  maxCards,
  renderCardItems,
  onIndexChange,
  showButtons,
  showChevronIcons = false,
  containerStyle,
  flatListContainerStyle,
  buttonStyle,
  shouldShowButtonsInTopRight = false,
  showCarouselButtonSkeleton = false,
}: CarouselSliderProps<T>) => {
  const viewPortWidth = Dimensions.get('window').width
  const flatListRef = useRef<FlatList<T> | null>(null)
  const { formatMessage } = useIntl()
  const [lastIndex, setLastIndex] = useState<number>(0)
  const { colors } = useTheme() as ThemeType

  useEffect(() => {
    const listOfFields = flatListRef.current

    if (listOfFields) {
      listOfFields.scrollToIndex({ index: 0, animated: false })
      setLastIndex(0)
    }
  }, [viewPortWidth])

  /**
   * Increment or decrement to new index
   *
   * @param incrementInterval : number
   */
  const scrollToCardIndex = (incrementInterval: number) => {
    const listOfFields = flatListRef.current
    if (listOfFields) {
      const offsetIndex = lastIndex + incrementInterval * maxCards
      let newIndex = 0
      if (offsetIndex <= 0) {
        listOfFields.scrollToIndex({ index: 0, animated: true })
      } else if (offsetIndex >= cards.length - 1) {
        listOfFields.scrollToIndex({ index: cards.length - 1, animated: true })
        newIndex = cards.length - 1
      } else {
        listOfFields.scrollToIndex({ index: offsetIndex, animated: true })
        newIndex = offsetIndex
      }
      setLastIndex(newIndex)
      onIndexChange(newIndex)
    }
  }

  return (
    <BodyContainer testID={tID('CarouselSlider')} style={containerStyle}>
      <FlatListContainer showChevronIcons={showChevronIcons} style={flatListContainerStyle}>
        <FlatList ref={flatListRef} data={cards} renderItem={renderCardItems} horizontal scrollEnabled={false} />
      </FlatListContainer>
      {showButtons && (
        <CarouselButtonContainer
          showChevronIcons={showChevronIcons}
          shouldShowButtonsInTopRight={shouldShowButtonsInTopRight}
        >
          {showCarouselButtonSkeleton ? (
            <BaseSkeleton
              layout={[
                {
                  key: 'carouselButtons',
                  width: 104,
                  height: 40,
                  marginBottom: 24,
                },
              ]}
            />
          ) : (
            <>
              <CarouselLeftButtonContainer showChevronIcons={showChevronIcons}>
                <SecondaryTertiaryIconButton
                  onPress={() => {
                    scrollToCardIndex(-1)
                  }}
                  testID={
                    showChevronIcons
                      ? tID('CarouselSlider-carouselSliderChevronLeftButton')
                      : tID('CarouselSlider-carouselSliderLeftButton')
                  }
                  leftIcon={
                    showChevronIcons ? (
                      <ChevronIcon fillColor={colors.textPrimary} size={24} direction={ChevronIconDirection.LEFT} />
                    ) : (
                      <ArrowIcon direction={ArrowIconDirection.LEFT} />
                    )
                  }
                  accessibilityLabel={formatMessage({
                    defaultMessage: 'Carousel slider left button',
                    description: 'Carousel slider left button',
                  })}
                  style={buttonStyle}
                  disabled={lastIndex === 0}
                  {...(showChevronIcons && { size: ButtonSize.SMALL })}
                />
              </CarouselLeftButtonContainer>
              <SecondaryTertiaryIconButton
                onPress={() => {
                  scrollToCardIndex(1)
                }}
                testID={
                  showChevronIcons
                    ? tID('CarouselSlider-carouselSliderChevronRightButton')
                    : tID('CarouselSlider-carouselSliderRightButton')
                }
                leftIcon={
                  showChevronIcons ? (
                    <ChevronIcon fillColor={colors.textPrimary} size={24} direction={ChevronIconDirection.RIGHT} />
                  ) : (
                    <ArrowIcon direction={ArrowIconDirection.RIGHT} />
                  )
                }
                accessibilityLabel={formatMessage({
                  defaultMessage: 'Carousel slider right button',
                  description: 'Carousel slider right button',
                })}
                style={buttonStyle}
                disabled={lastIndex > (cards.length - 1) / maxCards}
                {...(showChevronIcons && { size: ButtonSize.SMALL })}
              />
            </>
          )}
        </CarouselButtonContainer>
      )}
    </BodyContainer>
  )
}
